import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, IconButton } from '@mui/material'
import { TAB_KEYS, prettyAttr } from '../utils/consts'
import PrintIcon from '@mui/icons-material/Print'
import { exportControlsButton } from './layout.styles'

type ExportControlsProps = {
  rootEl: HTMLElement | null
  tabKey?: TAB_KEYS
}

const DEFAULT_SELECTORS = ['[data-exportable-chart="true"]']

const TAB_SPECIFIC_SELECTORS: Partial<Record<TAB_KEYS, string[]>> = {
  // Customize per tab if needed in the future
}

function collectExportNodes(
  root: HTMLElement,
  selectors: string[]
): HTMLElement[] {
  const unique = new Set<HTMLElement>()
  for (const selector of selectors) {
    root.querySelectorAll<HTMLElement>(selector).forEach((el) => {
      // Filter out invisible elements
      const style = window.getComputedStyle(el)
      if (style.display === 'none' || style.visibility === 'hidden') return
      unique.add(el)
    })
  }

  // Prefer top-level containers only (avoid duplicates from nested matches)
  const candidates = Array.from(unique)
  const topLevel = candidates.filter(
    (el) => !candidates.some((other) => other !== el && other.contains(el))
  )
  if (topLevel.length === 0) return [root]
  // Sort top-to-bottom by Y position to ensure 1xN stacking order
  return topLevel.sort(
    (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
  )
}

async function exportNodesToPdf(nodes: HTMLElement[], title?: string) {
  const [{ jsPDF }, html2canvas] = await Promise.all([
    import('jspdf'),
    import('html2canvas').then((m) => m.default ?? (m as any)),
  ])

  const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 28.35 // 10mm in points
  const contentWidth = pageWidth - 2 * margin
  const contentHeight = pageHeight - 2 * margin

  let currentPage = 0

  if (title && title.trim()) {
    pdf.setFontSize(18)
    pdf.setFont('helvetica', 'bold')
    pdf.text(title, margin, margin + 15)
  }

  for (let i = 0; i < nodes.length; i += 2) {
    if (i > 0) {
      pdf.addPage()
      currentPage++
    }

    const startY = i === 0 ? margin + 30 : margin
    const availableHeight = i === 0 ? contentHeight - 30 : contentHeight
    const chartHeight = (availableHeight - 8) / 2

    nodes[i].scrollIntoView({ block: 'nearest' })
    const noPrintElements1 = nodes[i].querySelectorAll('.no-print')
    const originalDisplays1: string[] = []
    noPrintElements1.forEach((el) => {
      const htmlEl = el as HTMLElement
      originalDisplays1.push(htmlEl.style.display)
      htmlEl.style.display = 'none'
    })

    const canvas1 = await html2canvas(nodes[i], {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
    })

    // Restore .no-print elements
    noPrintElements1.forEach((el, idx) => {
      ;(el as HTMLElement).style.display = originalDisplays1[idx]
    })

    const imgData1 = canvas1.toDataURL('image/png')
    pdf.addImage(
      imgData1,
      'PNG',
      margin,
      startY,
      contentWidth,
      chartHeight,
      undefined,
      'FAST'
    )

    // Render the second chart if it exists
    if (i + 1 < nodes.length) {
      nodes[i + 1].scrollIntoView({ block: 'nearest' })
      const noPrintElements2 = nodes[i + 1].querySelectorAll('.no-print')
      const originalDisplays2: string[] = []
      noPrintElements2.forEach((el) => {
        const htmlEl = el as HTMLElement
        originalDisplays2.push(htmlEl.style.display)
        htmlEl.style.display = 'none'
      })

      const canvas2 = await html2canvas(nodes[i + 1], {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      })

      noPrintElements2.forEach((el, idx) => {
        ;(el as HTMLElement).style.display = originalDisplays2[idx]
      })

      const imgData2 = canvas2.toDataURL('image/png')
      pdf.addImage(
        imgData2,
        'PNG',
        margin,
        startY + chartHeight + 8,
        contentWidth,
        chartHeight,
        undefined,
        'FAST'
      )
    }
  }

  pdf.save('data-analysis.pdf')
}

function openPrintWindow(nodes: HTMLElement[], title?: string) {
  const printWindow = window.open('', '_blank')
  if (!printWindow) return

  const doc = printWindow.document

  const style = `
    <style>
      @page { size: A4 portrait; margin: 10mm; }

      body {
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 0;
      }

      .container {
        width: 100%;
        display: block;
      }

      .export-title {
        font-size: 18pt;
        font-weight: 700;
        margin: 0 0 20pt 0;
        page-break-after: avoid;
      }

      .chart-block {
        width: 100%;
        margin-bottom: 24pt;
        page-break-inside: avoid;
        break-inside: avoid;
      }

      .chart-title {
        font-size: 12pt;
        font-weight: 600;
        margin-bottom: 8pt;
        page-break-after: avoid;
      }

      .export-block {
        width: 100%;
        display: block;
      }

      .export-block > * {
        max-width: 100%;
        height: auto !important;
      }

      img, canvas, svg {
        max-width: 100% !important;
        height: auto !important;
      }

      .no-print {
        display: none !important;
      }
    </style>
  `

  doc.write(
    '<html lang=""><head><title>Print</title>' + style + '</head><body>'
  )

  if (title && title.trim()) {
    const h1 = doc.createElement('div')
    h1.className = 'export-title'
    h1.textContent = title
    doc.body.appendChild(h1)
  }

  const container = doc.createElement('div')
  container.className = 'container'
  doc.body.appendChild(container)

  nodes.forEach((node) => {
    const block = doc.createElement('div')
    block.className = 'chart-block'

    const titleEl = doc.createElement('div')
    titleEl.className = 'chart-title'

    block.appendChild(titleEl)

    const wrapper = doc.createElement('div')
    wrapper.className = 'export-block'

    const cloned = node.cloneNode(true) as HTMLElement
    wrapper.appendChild(cloned)

    block.appendChild(wrapper)
    container.appendChild(block)
  })

  doc.close()

  const finalizeAndPrint = () => {
    printWindow.requestAnimationFrame(() => {
      printWindow.requestAnimationFrame(() => {
        printWindow.focus()
        printWindow.print()
      })
    })
  }

  if (doc.readyState === 'complete') {
    finalizeAndPrint()
  } else {
    printWindow.addEventListener('load', finalizeAndPrint, { once: true })
  }

  printWindow.addEventListener('afterprint', () => {
    printWindow.close()
  })
}

export const ExportControls: React.FC<ExportControlsProps> = ({
  rootEl,
  tabKey,
}) => {
  const selectors = useMemo(() => {
    const tabSpecific = (tabKey && TAB_SPECIFIC_SELECTORS[tabKey]) || []
    return [...tabSpecific, ...DEFAULT_SELECTORS]
  }, [tabKey])

  const [hasExportable, setHasExportable] = useState(false)

  useEffect(() => {
    if (!rootEl) {
      setHasExportable(false)
      return
    }

    const recompute = () => {
      try {
        const nodes = collectExportNodes(rootEl, selectors)
        const has = !(nodes.length === 1 && nodes[0] === rootEl)
        setHasExportable(has)
      } catch {
        setHasExportable(false)
      }
    }

    recompute()

    const observer = new MutationObserver(() => {
      recompute()
    })

    observer.observe(rootEl, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: false,
    })

    window.addEventListener('resize', recompute)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', recompute)
    }
  }, [rootEl, selectors])

  const handleExportPdf = useCallback(async () => {
    if (!rootEl || !hasExportable) return
    const nodes = collectExportNodes(rootEl, selectors)
    const title = tabKey ? prettyAttr(tabKey) : 'Data Analysis'
    await exportNodesToPdf(nodes, title)
  }, [rootEl, selectors, hasExportable, tabKey])

  const handlePrint = useCallback(() => {
    if (!rootEl || !hasExportable) return
    const nodes = collectExportNodes(rootEl, selectors)
    const title = tabKey ? prettyAttr(tabKey) : 'Data Analysis'
    openPrintWindow(nodes, title)
  }, [rootEl, selectors, hasExportable, tabKey])

  return (
    <Box
      sx={{
        position: 'absolute',
        right: 20,
        top: 0,
        display: 'flex',
        gap: 1,
        p: 2,
        zIndex: 2,
      }}
    >
      <IconButton
        size="small"
        onClick={handlePrint}
        sx={exportControlsButton}
        disabled={!hasExportable}
        aria-label="Print charts"
        title={!hasExportable ? 'No charts to print' : 'Print'}
      >
        <PrintIcon />
      </IconButton>
      {/*<IconButton*/}
      {/*  size="small"*/}
      {/*  onClick={handleExportPdf}*/}
      {/*  sx={exportControlsButton}*/}
      {/*  disabled={!hasExportable}*/}
      {/*  aria-label="Export charts to PDF"*/}
      {/*  title={!hasExportable ? 'No charts to export' : 'Export to PDF'}*/}
      {/*>*/}
      {/*  <PictureAsPdfIcon />*/}
      {/*</IconButton>*/}
    </Box>
  )
}

export default ExportControls
